'use client'

import { Fragment, ReactNode, useEffect, useState } from 'react'
import { Button, Spacer, Text } from '@vinted/web-ui'
import { HappyFaceFilled16, SadFaceFilled16 } from '@vinted/monochrome-icons'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { FeedbackFormModal } from '@marketplace-web/shared/ui-helpers'

import {
  FaqFeedback,
  userSubmitFaqFeedbackEvent,
  updateFaqEntryFeedbackForm,
  getFaqEntryFeedbackData,
  transformFeedbackFormRatingsData,
} from '@marketplace-web/help-center/help-center-faq-entry-data'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'
import {
  UpdateFeedbackRatingOptionsArgs,
  FeedbackRatingModel,
} from '@marketplace-web/conversation-feedback/conversation-feedback-data'

const feedbackMap = {
  4: FaqFeedback.Positive,
  2: FaqFeedback.Negative,
}

type Props = {
  faqId: number
  faqEntryChannel?: string
  faqAccessChannel?: string
}

const HelpCenterFaqFeedbackForm = ({ faqId, faqEntryChannel, faqAccessChannel }: Props) => {
  const translate = useTranslate('help_center.faq_feedback_form')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [feedbackUuid, setFeedbackUuid] = useState<string>()
  const [feedbackRating, setFeedbackRating] = useState<number>()
  const [ratings, setRatings] = useState<FeedbackRatingModel>()

  const { access_channel: accessChannel } = useBrowserNavigation().searchParams

  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  const { track } = useTracking()

  useEffect(() => {
    const getFeedbacksOptions = async () => {
      const response = await getFaqEntryFeedbackData(faqId)

      if ('errors' in response) return

      const transformedResponse = transformFeedbackFormRatingsData(response.feedback_ratings)

      setFeedbackUuid(response.feedback_uuid)
      setRatings(transformedResponse)
    }

    getFeedbacksOptions()
  }, [faqId])

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleFeedbackSubmit = async ({
    rating,
    message,
    feedbackStatementIds,
    finalized,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (!feedbackUuid) return

    await updateFaqEntryFeedbackForm({
      accessChannel: typeof accessChannel === 'string' ? accessChannel : undefined,
      message,
      feedbackUuid,
      faqId,
      rating,
      feedbackStatementIds,
      finalized,
    })
  }

  const handleFeedbackUpdate = async ({
    rating,
    message,
    feedbackStatementIds,
    finalized,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (finalized) {
      setIsModalOpen(false)

      await handleFeedbackSubmit({ rating, message, feedbackStatementIds, finalized })

      return
    }

    await handleFeedbackSubmit({ rating })
  }

  const handleOnLinkClick = (rating: number) => async () => {
    track(
      userSubmitFaqFeedbackEvent({
        faqId,
        helpCenterSessionId,
        faqEntryChannel,
        faqAccessChannel,
        faqFeedback: feedbackMap[rating],
      }),
    )
    setFeedbackRating(rating)
    setIsModalOpen(true)

    await handleFeedbackSubmit({ rating })
  }

  const renderLink = (chunks: Array<ReactNode>, id: number) => {
    const iconName = id === 4 ? HappyFaceFilled16 : SadFaceFilled16
    const iconTestId = id === 4 ? 'happy-face' : 'sad-face'

    return (
      <Fragment key={`link-${id}`}>
        <Spacer
          orientation={Spacer.Orientation.Vertical}
          // size={id === 4 ? Spacer.Size.X3Large : Spacer.Size.Regular}
        />
        <Button
          inline
          iconName={iconName}
          size={Button.Size.Medium}
          onClick={handleOnLinkClick(id)}
          testId={`feedback-button-${iconTestId}`}
        >
          {chunks}
        </Button>
      </Fragment>
    )
  }

  const renderFeedbackText = () => {
    const translationValues = {
      'no-link': (chunks: Array<ReactNode>) => renderLink(chunks, 2),
      'yes-link': (chunks: Array<ReactNode>) => renderLink(chunks, 4),
    }

    return feedbackRating ? (
      <Text type={Text.Type.Body} bold as="div" text={translate('answer')} />
    ) : (
      <div className="faq__feedback-rating-buttons">
        {!feedbackRating && <>{translate('question', translationValues)}</>}
      </div>
    )
  }

  return (
    <>
      {renderFeedbackText()}
      <FeedbackFormModal
        selectedRating={feedbackRating}
        isOpen={isModalOpen}
        ratings={ratings}
        onClose={handleModalClose}
        onSubmit={handleFeedbackUpdate}
      />
    </>
  )
}

export default HelpCenterFaqFeedbackForm
